import { StoreType } from "@omni/types/api/store/cloud/shared/enums/storeType.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { throttle } from "lodash-es";
import { ReactNode, useMemo, useRef, useState } from "react";
import { GroupBase, OptionsOrGroups, SingleValue } from "react-select";
import Select from "react-select/base";

import { StoreSearchInputContextProvider } from "@/components/Store/StoreSearchInput/context/storeSearchInput.context";
import {
    fetchAddressSearchItems,
    fetchStoreSearchItems
} from "@/components/Store/StoreSearchInput/storeSearchInput.helpers";
import { SearchItemType } from "@/components/Store/StoreSearchInput/storeSearchInput.types";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { useWoosmapScript } from "@/services/woosmap/woosmap.initializer";
import { ScriptLoadingStates } from "@/utils/hooks/useScript.hook";
import { useWoosmapServices } from "@/utils/hooks/useWoosmapServices.hook";

import { SearchItem } from "../storeSearchInput.types";

type BaseProps = {
    messageId: string;
    value?: SearchItem;
    clearable: boolean;
    itemsToRetrieve: Map<SearchItemType, number>;
    placeholder: string;
    loadingText: string;
    menuPortalTarget: HTMLElement | null;
    "aria-label": string;
    onClearValue?: () => void;
    onUseLocation: (latitude: number, longitude: number) => void;
    onSelect: (value: SearchItem | null) => void;
    onSubmitClick?: (value?: string) => void;
    children: ReactNode;
    storeTypes?: Array<StoreType>;
};

export const Base = ({
    messageId,
    value,
    clearable,
    itemsToRetrieve,
    placeholder,
    loadingText,
    menuPortalTarget,
    "aria-label": ariaLabel,
    onClearValue,
    onUseLocation,
    onSelect,
    onSubmitClick,
    storeTypes,
    children
}: BaseProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isFocussed, setIsFocussed] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const focusRef = useRef<SearchItem>(null);
    const asyncRef =
        useRef<Select<SearchItem, false, GroupBase<SearchItem>>>(null);
    const inputValueRef = useRef<string | undefined>(undefined);
    const setOptionsCallbackRef =
        useRef<
            (
                options: OptionsOrGroups<SearchItem, GroupBase<SearchItem>>
            ) => void | undefined
        >(undefined);

    const woosmapScriptLoadingState = useWoosmapScript();

    const { LocalitiesService, StoresService } = useWoosmapServices();

    const disabled = woosmapScriptLoadingState !== ScriptLoadingStates.Ready;

    const updateOptions = useMemo(() => {
        return throttle(async () => {
            const inputValue = inputValueRef.current;
            const setOptions = setOptionsCallbackRef.current;

            if (
                !isDefined(inputValue) ||
                !isDefined(setOptions) ||
                !isDefined(StoresService) ||
                !isDefined(LocalitiesService)
            ) {
                return;
            }

            if (inputValue.length === 0) return setOptions([]);

            try {
                setIsLoading(true);

                const searchItemPromises = [];

                if (itemsToRetrieve.has(SearchItemType.Store)) {
                    searchItemPromises.push(
                        fetchStoreSearchItems(
                            StoresService,
                            inputValue,
                            itemsToRetrieve.get(SearchItemType.Store),
                            storeTypes
                        )
                    );
                }

                if (itemsToRetrieve.has(SearchItemType.Locality)) {
                    searchItemPromises.push(
                        fetchAddressSearchItems(
                            LocalitiesService,
                            inputValue,
                            itemsToRetrieve.get(SearchItemType.Locality)
                        )
                    );
                }

                const results = (await Promise.all(searchItemPromises)).flat();

                setOptions(results);
            } catch {
                setOptions([]);
            } finally {
                setIsLoading(false);
            }
        }, 700);
    }, [
        itemsToRetrieve.get,
        itemsToRetrieve.has,
        StoresService,
        LocalitiesService,
        storeTypes
    ]);

    const loadOptions = (
        inputValue: string,
        callback: (
            options: OptionsOrGroups<SearchItem, GroupBase<SearchItem>>
        ) => void
    ) => {
        inputValueRef.current = inputValue;
        setOptionsCallbackRef.current = callback;

        updateOptions();
    };

    const handleOnChange = (newValue: SingleValue<SearchItem>) => {
        onSelect(newValue);
        setIsFocussed(false);

        focusRef.current = null;
    };

    const handleOnInputChange = (newValue: string) => {
        if (newValue) {
            inputValueRef.current = newValue;
        }

        setIsOpen(!!newValue);
    };

    const handleOnFocus = () => {
        if (asyncRef.current) {
            asyncRef.current.clearValue();
        }

        onClearValue?.();
        setIsFocussed(true);
    };

    const handleOnBlur = () => {
        setIsOpen(false);
        setIsFocussed(false);
    };

    const handleOnSearchClick = () => {
        if (inputValueRef.current) {
            onSubmitClick?.(inputValueRef.current);
        }

        if (isDefined(focusRef.current)) {
            onSelect(focusRef.current);
        }
    };

    const handleOnLocationError = (message: string) =>
        broadcastMessage({
            id: messageId,
            text: message,
            status: "danger",
            dismissible: true
        });

    return (
        <StoreSearchInputContextProvider
            disabled={disabled}
            focussed={isFocussed}
            open={isOpen}
            loading={isLoading}
            clearable={clearable}
            asyncRef={asyncRef}
            focusRef={focusRef}
            menuPortalTarget={menuPortalTarget}
            value={value}
            placeholder={placeholder}
            loadingText={loadingText}
            aria-label={ariaLabel}
            loadOptions={loadOptions}
            onSearchClick={handleOnSearchClick}
            onUseLocation={onUseLocation}
            onLocationError={handleOnLocationError}
            onChange={handleOnChange}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            onInputChange={handleOnInputChange}
        >
            {children}
        </StoreSearchInputContextProvider>
    );
};
